import type { OrchestrationSession, OrchestrationThreadActivity, TurnId } from "@t3tools/contracts";

export type CanonicalPlanStepStatus = "pending" | "inProgress" | "completed";

export interface CanonicalPlanSnapshot {
  readonly revision: string;
  readonly createdAt: string;
  readonly turnId: TurnId | null;
  readonly explanation: string | null;
  readonly steps: ReadonlyArray<{
    readonly step: string;
    readonly status: CanonicalPlanStepStatus;
  }>;
}

export type CanonicalPlanLiveTurn = Pick<OrchestrationSession, "activeTurnId" | "status">;

function comparePlanActivityOrder(
  left: OrchestrationThreadActivity,
  right: OrchestrationThreadActivity,
): number {
  if (left.sequence !== undefined && right.sequence !== undefined) {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
  } else if (left.sequence !== undefined) {
    return 1;
  } else if (right.sequence !== undefined) {
    return -1;
  }

  // Without a durable server sequence, the materialized canonical timeline
  // array is already the ordering authority. Native revision ids and Provider
  // timestamps are identity/metadata, not a second ordering source.
  return 0;
}

export function deriveCanonicalPlanSnapshot(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): CanonicalPlanSnapshot | null {
  let latest: OrchestrationThreadActivity | null = null;
  for (const activity of activities) {
    if (activity.kind !== "turn.plan.updated") {
      continue;
    }
    if (latest === null || comparePlanActivityOrder(latest, activity) <= 0) {
      latest = activity;
    }
  }
  if (latest === null) {
    return null;
  }

  const payload =
    latest.payload && typeof latest.payload === "object"
      ? (latest.payload as Record<string, unknown>)
      : null;
  if (!Array.isArray(payload?.plan)) {
    return null;
  }

  const steps: Array<CanonicalPlanSnapshot["steps"][number]> = [];
  for (const value of payload.plan) {
    if (!value || typeof value !== "object") {
      continue;
    }
    const step = value as Record<string, unknown>;
    if (typeof step.step !== "string" || step.step.trim().length === 0) {
      continue;
    }
    steps.push({
      step: step.step.trim(),
      status: step.status === "completed" || step.status === "inProgress" ? step.status : "pending",
    });
  }

  return {
    revision: latest.id,
    createdAt: latest.createdAt,
    turnId: latest.turnId,
    explanation:
      typeof payload.explanation === "string" && payload.explanation.trim().length > 0
        ? payload.explanation.trim()
        : null,
    steps,
  };
}

export function deriveActiveTurnPlanSnapshot(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  liveTurn: CanonicalPlanLiveTurn | null,
): CanonicalPlanSnapshot | null {
  if (
    liveTurn === null ||
    (liveTurn.status !== "starting" && liveTurn.status !== "running") ||
    liveTurn.activeTurnId === null
  ) {
    return null;
  }

  const snapshot = deriveCanonicalPlanSnapshot(activities);
  if (
    snapshot === null ||
    snapshot.turnId !== liveTurn.activeTurnId ||
    snapshot.steps.length === 0
  ) {
    return null;
  }

  return snapshot;
}
